import { StatementBankAccount } from "./statementTypes";

/**
 * A reference means the funds route via a shared platform account.
 *
 * Mirrors `isPooledMethod` in `utils/settlementOwnership.ts`, which applies
 * the same rule to the generated `FiatSettlementMethod`. The two are kept
 * separate because they take different types — the API model and the flat
 * statement view model — not because the rule differs.
 *
 * Caveat worth knowing: `reference` is optional in the OpenAPI contract and
 * there is no pooled/account-type discriminator, so this heuristic is the best
 * signal available. A pooled account arriving without a reference would be
 * treated as personal.
 */
export const isPooledStatementAccount = (
  account: StatementBankAccount
): boolean => !!account.reference;

/**
 * The account name to print beneath the customer's own name in the statement's
 * "Customer information" block, or `undefined` to print nothing.
 *
 * A statement is a formal financial document and this block asserts whose it
 * is. A pooled account is shared platform infrastructure, so its name must
 * never appear here — a bank or auditor reading the statement would attribute
 * it to the customer. Only a personal account can supply this name, and only
 * when it actually adds information the customer's own name doesn't already
 * carry.
 */
export const resolveCustomerDisplayAccountName = (
  bankAccounts: StatementBankAccount[],
  customerName: string
): string | undefined => {
  const personalAccountName = bankAccounts.find(
    (account) => !isPooledStatementAccount(account)
  )?.accountName;

  if (!personalAccountName || personalAccountName === customerName) {
    return undefined;
  }

  return personalAccountName;
};
